import {useCallback, useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import classNames from 'classnames';

import type {ControlOption} from 'shells/card/segmented-control';
import {SegmentedControl} from 'shells/card/segmented-control';
import {Stack} from 'shells/layout/stack/stack';

import {addNotification} from '../../../notifications/index';

import {TAB_ACTIVITY, isFormTabSelected} from '../redux/consts';
import {setCreatorFormActive} from '../redux/core-entity-page-reducer';
import {getCoreEntityPageCreatorForm} from '../redux/core-entity-page-selectors';
import type {TabOption} from '../redux/types';

import {CreateTimelineEventForm} from './create-timeline-event-form';
import {getTimelineItemNotificationArgs} from './helpers';

import styles from './action-bar.module.css';

export type ActionBarOption = ControlOption<TabOption> & {
    onClick?: () => void;
};

type TimelineEventFormProps = {
    entityId: string;
    entityName: string;
    onAfterSubmit?: (id: string, activityTypeName?: string, noteBody?: string) => void;
    openOnTrigger?: number;
    canOpen?: boolean;
};

type Props = {
    options: ActionBarOption[];
    selectedTab: TabOption;
    onSelectTab: (tab: TabOption) => void;
    entityDisplayName: string;
    children?: React.ReactNode;
    getRef?: (ref: HTMLDivElement | null) => void;
    timelineEventFormProps?: TimelineEventFormProps;
};

export function ActionBar(props: Props) {
    const {
        options,
        selectedTab,
        onSelectTab,
        entityDisplayName,
        children,
        getRef,
        timelineEventFormProps,
    } = props;
    const dispatch = useDispatch();

    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [formOpenTrigger, setFormOpenTrigger] = useState<number>(0);
    const {notePlaintextValue, isActive: isCreatorFormActive} = useSelector(
        getCoreEntityPageCreatorForm
    );

    // Open form when creator form is active, but only when allowed to open.
    // Prevents mini header interactions from triggering auto-focus in the main
    // action bar (which would scroll the user back up) when scrolled past checkpoint.
    useEffect(() => {
        if (timelineEventFormProps?.canOpen ?? true) {
            setIsFormOpen(isCreatorFormActive);
        }
    }, [isCreatorFormActive, timelineEventFormProps?.canOpen]);

    const handleSelectTab = useCallback(
        (tab: TabOption) => {
            if (isFormTabSelected(tab)) {
                if (tab === selectedTab) {
                    setFormOpenTrigger((prev) => (prev > 0 ? 0 : prev + 1));
                } else {
                    setFormOpenTrigger((prev) => prev + 1);
                }
            }

            onSelectTab(tab);
        },
        [onSelectTab, selectedTab]
    );

    useEffect(() => {
        if (
            (timelineEventFormProps?.openOnTrigger && timelineEventFormProps.openOnTrigger > 0) ||
            formOpenTrigger > 0
        ) {
            dispatch(setCreatorFormActive(true));
        }
    }, [timelineEventFormProps?.openOnTrigger, formOpenTrigger, dispatch]);

    const handleFormClose = useCallback(() => {
        if (notePlaintextValue.trim() === '') {
            dispatch(setCreatorFormActive(false));
        }
    }, [dispatch, notePlaintextValue]);

    const handleOnOpenChange = (isOpen: boolean) => {
        setIsFormOpen(isOpen);
        if (!isOpen) {
            handleFormClose();
        }
    };
    const handleAfterSubmit = useCallback(
        (id: string, activityTypeName?: string, noteBody?: string) => {
            dispatch(setCreatorFormActive(false));
            timelineEventFormProps?.onAfterSubmit?.(id, activityTypeName, noteBody);
            const contentType = selectedTab === TAB_ACTIVITY ? 'ACTIVITY' : 'NOTE';
            const {title, options} = getTimelineItemNotificationArgs(
                contentType,
                id,
                entityDisplayName,
                activityTypeName,
                noteBody
            );
            addNotification(title, options);
        },
        [dispatch, timelineEventFormProps, selectedTab, entityDisplayName]
    );

    const shouldRenderTimelineEventForm =
        timelineEventFormProps !== undefined && isFormTabSelected(selectedTab);

    return (
        <div
            className={classNames('pb-16 pl-16', {
                'pt-16': !NutClientConfig.hasCoreEntityFullHeightSidebarFF,
            })}
            ref={getRef}
        >
            <Stack spacing={12} isFullWidth={true}>
                <div className={styles['action-bar-tab-controls']}>
                    <SegmentedControl
                        buttonOptions={options}
                        selectedValue={selectedTab}
                        setSelectedValue={handleSelectTab}
                        isAnimated={true}
                        hasPadding={false}
                    />
                </div>
                {shouldRenderTimelineEventForm && (
                    <CreateTimelineEventForm
                        entityId={timelineEventFormProps.entityId}
                        entityName={timelineEventFormProps.entityName}
                        contentType={selectedTab === TAB_ACTIVITY ? 'ACTIVITY' : 'NOTE'}
                        showPlaceholder={true}
                        onAfterSubmit={handleAfterSubmit}
                        isOpen={isFormOpen}
                        onOpenChange={handleOnOpenChange}
                        onFocus={() => dispatch(setCreatorFormActive(true))}
                    />
                )}
                {children}
            </Stack>
        </div>
    );
}
